import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { IOutSelectQuote } from "app_locobook/pages/quote-page/components/quote-list-container/quote-list-container.component";
import { PropalService } from 'app_locobook/service/propal/propal.service';
import { ILabels } from "libraries/interfaces/ILabels";
import { NotificationType } from 'libraries/interfaces/INotification';
import { Yii } from "libraries/interfaces/Yii";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { ResourceError } from "libraries/service/resource/ResourceError";
import { AirTripResourceService } from "libraries/service/resources/airTrip/airTripResource.service";
import { IAirQuoteFilter, IQuoteQuantityCollection } from "libraries/service/resources/airTrip/IAirQuoteFilter";
import { IGetAirTripQuotes, IGetAirTripQuotesDictionary, IQuotes } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { of, Subject } from "rxjs";
import { debounceTime, flatMap } from 'rxjs/operators';
import { HostAliasResource, IHostAlias } from "../../../libraries/service/resources/host-alias/host-alias.resource";
import { IOnAlternativeSelectOutput } from './components/fare-family-benchmark/fare-family-benchmark.component';
import { IFilterFormValue } from "./components/quote-filter/quote-filter.component";
import { FilterService } from "./services/filter/filter.service";
import { IFilterResult } from "./services/filter/IFilterResult";

@Component({
    selector: 'quote-list',
    template: `
        <div class="cols">
            <quote-filter class="flex-2"
                          [filterData]="filters"
                          [dictionary]="dictionary"
                          (onChangeFilter)="onFilterChange.next($event)">
            </quote-filter>
            <quote-list-container class="flex-6"
                                  [tripId]="tripId"
                                  [quotes]="filteredQuotes"
                                  [selectedQuoteId]="selectedQuoteId"
                                  [originalRequest]="originalRequest"
                                  [price]="price"
                                  [airports]="dictionary ? dictionary.airport: null"
                                  [carriers]="dictionary ? dictionary.carrier: null"
                                  [fareFamilyDictionary]="dictionary ? dictionary.fareFamily: null"
                                  [isOfferSelection]="isOfferSelection"
                                  [isModifySegment]="isModifySegment"
                                  [sessionPostOnUpdateBooking]="sessionPostOnUpdateBooking"
                                  (onSelectedQuote)="selectQuote($event)"
                                  (onSelectedQuoteOnly)="onSelectedQuoteOnly.emit($event)"
                                  (onSelectOtherFlight)="onSelectOtherFlight.emit($event)"
                                  (onAlternativeSelect)="alternativeSelect($event)"
                                  [vccPermission]="vccPermission"
                                  [ndcPermission]="ndcPermission"
                                  >
                <div class="notification-wrapper">
                    <loader *ngIf="loading && !errorMessage"
                            [message]="wording.loading"></loader>
                    <notification *ngIf="hasError || errorMessage"
                                  [type]="notificationType.Error">
                        {{errorMessage}}
                    </notification>
                    <notification *ngIf="!loading && !hasQuotes && !hasError"
                                  [type]="notificationType.Info">
                        {{ wording.noResults }}
                    </notification>
                </div>
            </quote-list-container>
        </div>
    `,
    styles: [`
        .cols {
            display: flex;
        }

        .cols > :last-child {
            margin-left: 15px;
        }
    `]
})
export class QuoteListComponent implements OnInit, OnDestroy, OnChanges {

    @Input()
    public tripId: string;

    @Input()
    public selectedQuoteId: string;

    @Input()
    public originalRequest: any;

    @Input()
    public sessionPostOnUpdateBooking: any = null;

    @Input()
    public errorMessage: string;
    @Input()
    public price: any;

    @Input()
    public isOfferSelection: boolean;

    @Input()
    public isModifySegment: boolean;

    @Input()
    public vccPermission: boolean;
    @Input()
    public ndcPermission: boolean;

    @Input()
    public mustRedirectAfterSelection: boolean = true;

    @Output()
    public readonly onClose: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onSelectQuote: EventEmitter<IOutSelectQuote> = new EventEmitter();
    @Output()
    public readonly onSelectedQuoteOnly: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();
    @Output()
    public readonly onAlternativeSelect: EventEmitter<IOnAlternativeSelectOutput> = new EventEmitter<IOnAlternativeSelectOutput>();
    @Output()
    public readonly onSelectOtherFlight: EventEmitter<IOutSelectQuote> = new EventEmitter<IOutSelectQuote>();

    public quotes: IQuotes;
    public filteredQuotes: IQuotes = null;

    public filters: IAirQuoteFilter = null;
    public dictionary: IGetAirTripQuotesDictionary;

    public loading: boolean = false;

    public wording: ILabels = {
        loading: Yii.t("wui-iqba-js", "Chargement des différentes propositions pour cette recherche."),
        noResults: Yii.t("wui-iqba-js", "Aucune proposition ne correspond à vos filtres"),
    };
    public hasError: boolean = false;
    public readonly notificationType: typeof NotificationType = NotificationType;
    public readonly onFilterChange: Subject<IFilterFormValue> = new Subject();
    public hasQuotes: boolean = false;

    constructor(
        protected readonly airTripResourceService: AirTripResourceService,
        protected readonly filterService: FilterService,
        protected readonly errorTracingService: ErrorTracingService,
        protected readonly hostAliasResource: HostAliasResource,
        protected readonly propalService: PropalService,
    ) {}

    public ngOnInit(): void {
        if (this.tripId) {
            this.getTripQuotes(this.tripId);
        } else {
            this.loading = true;
            this.hasQuotes = false;
            this.filteredQuotes = undefined;
        }

        this.onFilterChange.pipe(
            debounceTime(200))
            .subscribe({
                next: (value: IFilterFormValue): void => {
                    this.filterResults(value);
                }
            });
    }

    public ngOnDestroy(): void {
        this.onFilterChange.complete();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.tripId && changes.tripId.currentValue !== changes.tripId.previousValue && this.tripId) {
            this.getTripQuotes(this.tripId);
        } else if (!this.tripId) {
            this.loading = true;
            this.hasQuotes = false;
            this.filteredQuotes = undefined;
            this.filters = undefined;
            this.dictionary = undefined;
        }
    }

    public alternativeSelect($event: IOnAlternativeSelectOutput): void {
        this.onAlternativeSelect.emit($event);
    }

    public getTripQuotes(tripId: string): void {
        this.hasError = false;
        this.loading = true;
        this.hasQuotes = false;
        this.airTripResourceService.getTripQuotes(tripId)
            .pipe(
                flatMap((tripQuotes: IGetAirTripQuotes) => {
                        const hosts: string[] = Object.keys(tripQuotes.filter.host);
                        return this.hostAliasResource
                            .getAliases(hosts)
                            .pipe(
                                flatMap((hostAliases: IHostAlias[]) => {
                                    const hostAliasesFilters: IQuoteQuantityCollection = hosts
                                        .reduce((filters: IQuoteQuantityCollection, originalHost: string) => {

                                            const hostAlias: IHostAlias = hostAliases.find((item: IHostAlias) => item.host === originalHost);

                                            const filter: IQuoteQuantityCollection =
                                                hostAlias ?
                                                    {
                                                        [hostAlias.alias]: tripQuotes.filter.host[originalHost]
                                                    } :
                                                    {
                                                        [originalHost]: tripQuotes.filter.host[originalHost]
                                                    };

                                            return {
                                                ...filters,
                                                ...filter,
                                            };
                                        }, {});

                                    return of(
                                        {
                                            ...tripQuotes,
                                            filter: {
                                                ...tripQuotes.filter,
                                                host : hostAliasesFilters
                                            }
                                        }
                                    );
                                })
                            );
                    }
                )
            )
            .subscribe({
                next: (tripQuotes: IGetAirTripQuotes): void => {
                    this.loading = false;
                    this.propalService.setPropalResponseData(tripQuotes);
                    this.quotes = tripQuotes.booking;
                    this.filteredQuotes = tripQuotes.booking;
                    this.hasQuotes = this.filteredQuotes && Object.keys(this.filteredQuotes).length > 0;
                    this.filters = tripQuotes.filter;
                    this.dictionary = tripQuotes.dictionary;
                    this.getTripQuotesCallback(tripQuotes);
                },
                error: (error: ResourceError): void => {
                    this.errorTracingService.captureException(error);
                    this.loading = false;
                    this.hasError = true;
                    this.errorMessage = error.message;
                }
            });
    }

    public filterResults(formChange: IFilterFormValue): void {
        const filteredList: IFilterResult = this.getFilteredList(formChange);
        if (!filteredList.applied) {
            this.filteredQuotes = this.quotes;
        } else {
            this.filteredQuotes = Object
                .keys(this.quotes)
                .filter((quote: string): boolean => this.isQuoteInFilteredList(filteredList, quote))
                .reduce(
                    (acc: IQuotes, key: string) => ({...acc, [key]: this.quotes[key]}), {}
                );
        }

        this.hasQuotes = this.filteredQuotes && Object.keys(this.filteredQuotes).length > 0;
    }

    public closeQuoteList(): void {
        this.onClose.emit();
    }

    public selectQuote(quoteId: IOutSelectQuote): void {
        this.onSelectQuote.emit(quoteId);
    }

    /**
     * This method is necessary having a trigger inside getTripQuotes=>subscribe for components that extends QuoteListComponent
     * @param tripQuotes : IGetAirTripQuotes
     */
    protected getTripQuotesCallback(tripQuotes: IGetAirTripQuotes): void {}

    protected getFilteredList(formChange: IFilterFormValue): IFilterResult{
        return this.filterService.filter(this.filters, formChange);
    }

    protected isQuoteInFilteredList(filteredList: IFilterResult, quoteId: string): boolean{
        return filteredList.filtered.has(quoteId);
    }
}
